import { useState } from 'react';
import { X, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { COMMUNICATIONS_RETENTION_DAYS } from '../lib/retention';

type ExportFormat = 'csv' | 'pdf';
type DetailLevel = 'summary' | 'daily' | 'transactions';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  dateRange: { start: string; end: string };
}

const DETAIL_LEVELS: { value: DetailLevel; label: string; description: string }[] = [
  {
    value: 'summary',
    label: 'Summary',
    description: 'Category totals only (SMS, MMS, Voice, etc.)',
  },
  {
    value: 'daily',
    label: 'Daily Breakdown',
    description: 'Day-by-day totals for each category',
  },
  {
    value: 'transactions',
    label: 'Individual Transactions',
    description: 'Every message, call, and charge with full details (limited to first 50,000 records; SMS/MMS/voice detail retained for 45 days only)',
  },
];

export function ExportDialog({ isOpen, onClose, locationId, dateRange }: ExportDialogProps) {
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('summary');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);

    try {
      const url = `${import.meta.env.VITE_API_URL}/billing/${locationId}/export?start=${dateRange.start}&end=${dateRange.end}&format=${format}&level=${detailLevel}`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(errorData.error || 'Export failed');
      }

      // Get the filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `billing-report-${dateRange.start}-${dateRange.end}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) {
          filename = match[1];
        }
      }

      // Create blob and trigger download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // Close dialog on success
      onClose();
    } catch (err) {
      console.error('[ExportDialog] Download error:', err);
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md border-2 border-gray-300 max-h-[90vh] flex flex-col">
        {/* Header - stays fixed at top */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b-2 border-gray-300 bg-gray-50">
          <h2 className="text-lg font-bold text-gray-900">Export Report</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center border-2 border-gray-300 rounded hover:bg-gray-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Date Range Info */}
          <div className="bg-sky-50 border border-sky-400 rounded px-4 py-3 mb-6">
            <p className="text-sm text-sky-800">
              <span className="font-semibold">Date Range:</span>{' '}
              {formatDateDisplay(dateRange.start)} - {formatDateDisplay(dateRange.end)}
            </p>
          </div>

          {/* Format Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              Export Format
            </h3>
            <div className="flex gap-4">
              <label
                className={`flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded cursor-pointer transition-colors ${
                  format === 'csv'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={format === 'csv'}
                  onChange={() => setFormat('csv')}
                  className="w-4 h-4"
                />
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                <span className="font-semibold">CSV</span>
              </label>
              <label
                className={`flex-1 flex items-center justify-center gap-3 p-4 border-2 rounded cursor-pointer transition-colors ${
                  format === 'pdf'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="format"
                  value="pdf"
                  checked={format === 'pdf'}
                  onChange={() => setFormat('pdf')}
                  className="w-4 h-4"
                />
                <FileText className="w-5 h-5 text-red-600" />
                <span className="font-semibold">PDF</span>
              </label>
            </div>
          </div>

          {/* Detail Level Selection */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 mb-3 pb-2 border-b border-gray-200">
              Detail Level
            </h3>
            <div className="flex flex-col gap-3">
              {DETAIL_LEVELS.map((level) => (
                <label
                  key={level.value}
                  className={`flex items-start gap-3 p-4 border-2 rounded cursor-pointer transition-colors ${
                    detailLevel === level.value
                      ? 'border-gray-900 bg-gray-50'
                      : 'border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="detailLevel"
                    value={level.value}
                    checked={detailLevel === level.value}
                    onChange={() => setDetailLevel(level.value)}
                    className="w-4 h-4 mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="font-semibold text-gray-900">{level.label}</span>
                    <p className="text-sm text-gray-500 mt-1">{level.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Retention warning when transactions-level + range extends past retention */}
            {detailLevel === 'transactions' && (() => {
              const cutoff = Date.now() - COMMUNICATIONS_RETENTION_DAYS * 24 * 60 * 60 * 1000;
              const rangeStartTime = new Date(dateRange.start + 'T00:00:00').getTime();
              if (rangeStartTime < cutoff) {
                return (
                  <div className="mt-3 px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded">
                    Detail-level data is only retained for the last {COMMUNICATIONS_RETENTION_DAYS} days.
                    Your selected range extends beyond that, so the export will use the
                    Daily Breakdown level for the older portion to give you a complete file.
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer - stays fixed at bottom */}
        <div className="flex-shrink-0 flex justify-end gap-3 px-6 py-4 border-t-2 border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={downloading}
            className="px-4 py-2 border-2 border-gray-300 rounded font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white font-medium rounded hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Download
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
